// Lønn Subtab Utilities
// Handles file parsing, data filtering, and salary calculations

import { logger } from './logger';
import * as XLSX from 'xlsx';
import { LonnDataRow, KontantEdit } from './storage';

/**
 * Parse an Excel or CSV file into an array of data rows
 */
export async function parseFile(file: File): Promise<LonnDataRow[]> {
  const fileName = file.name.toLowerCase();

  if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
    return parseExcelFile(file);
  } else if (fileName.endsWith('.dat')) {
    return parseDatFile(file);
  } else if (fileName.endsWith('.csv')) {
    return parseCSVFile(file);
  } else {
    throw new Error('Unsupported file type. Please upload .xlsx, .xls, .csv, or .dat files.');
  }
}

/**
 * Parse an Excel file using xlsx library
 */
async function parseExcelFile(file: File): Promise<LonnDataRow[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });

        // Get the first worksheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          raw: false, // Keep values as strings for consistency
          defval: '', // Default value for empty cells
        }) as LonnDataRow[];

        resolve(jsonData);
      } catch (error) {
        reject(new Error(`Failed to parse Excel file: ${error}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read Excel file'));
    };

    reader.readAsArrayBuffer(file);
  });
}

/**
 * Parse a CSV file using PapaParse
 */
async function parseCSVFile(file: File): Promise<LonnDataRow[]> {
  const Papa = (await import('papaparse')).default;
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: false, // Keep all values as strings initially
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          logger.warn('system', 'CSV_PARSE_WARNINGS', 'CSV parsing warnings', { errors: results.errors as unknown as Record<string, unknown> });
        }
        resolve(results.data as LonnDataRow[]);
      },
      error: (error) => {
        reject(new Error(`Failed to parse CSV file: ${error.message}`));
      },
    });
  });
}

/**
 * Parse SKIFTL .dat file (backtick-delimited format with 169 fields)
 */
async function parseDatFile(file: File): Promise<LonnDataRow[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const rows = parseSkiftDat(content);
        resolve(rows);
      } catch (error) {
        reject(new Error(`Failed to parse .dat file: ${error}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read .dat file'));
    };

    reader.readAsText(file);
  });
}

/**
 * Helper: Parse Norwegian number format "15039,00" -> 15039
 */
function parseDatAmount(no: string): number {
  if (!no || no.trim() === '') return 0;
  // Remove thousands dots, replace decimal comma with dot
  const normalized = no.replace(/\./g, '').replace(',', '.');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Helper: Parse integer safely
 */
function parseDatInt(no: string): number {
  if (!no || no.trim() === '') return 0;
  const n = parseInt(no, 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Helper: Combine "01.12.2025" + "07.43" => "2025-12-01 07:43:00Z"
 */
function combineDatDateTime(dateStr: string, timeStr: string): string {
  const [d, m, y] = dateStr.split('.');
  const [hh, mm] = timeStr.split('.');
  return `${y}-${m}-${d} ${hh}:${mm}:00Z`;
}

/**
 * Core SKIFTL .dat parser
 * Format: backtick-delimited, 169 fields per line, no header row
 *
 * Full specification: "Taxi SKIFTL .dat File Structure – Full Specification (v1.0.0)"
 *
 * Key field mappings (see spec for complete list):
 * - fields[0]: empty
 * - fields[1]: "^31" (record type marker)
 * - fields[2]: Løyve (e.g. "R174")
 * - fields[3]: Sjaafor / driver ID (e.g. "1037")
 * - fields[4-7]: start/end date and time (DD.MM.YYYY, HH.mm)
 * - fields[8-11]: trip counts and distances
 * - fields[12-23]: turnover (credit, cash, fuel, etc.)
 * - fields[26]: Skiftnr
 * - fields[28-29]: cash register readings
 * - fields[31-42]: detailed trip/distance/credit counters
 * - fields[49-58]: salary, tax, owner share
 * - fields[65-79]: VAT counters and discounts
 * - fields[80-90]: VAT rates and org number
 * - fields[130-138]: work time and vehicle info
 * - fields[166-167]: salary base and corrected VAT
 * - fields[168]: "@11~31" (record terminator)
 *
 * This parser generates column names matching the Excel export format
 * to ensure identical data structure regardless of import source.
 */
export function parseSkiftDat(content: string): LonnDataRow[] {
  const lines = content.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const rows: LonnDataRow[] = [];

  for (const line of lines) {
    const fields = line.split('`');

    // Basic sanity checks: 169 fields, correct header and trailer
    if (fields.length !== 169) {
      logger.warn('system', 'DAT_PARSE_FIELD_COUNT', 'Skipping line with unexpected field count', { fieldCount: fields.length, expected: 169 });
      continue;
    }
    if (fields[1] !== '^31') {
      logger.warn('system', 'DAT_PARSE_INVALID_HEADER', 'Skipping line with invalid header marker', { marker: fields[1] });
      continue;
    }
    if (!fields[168].startsWith('@11~31')) {
      logger.warn('system', 'DAT_PARSE_INVALID_TRAILER', 'Skipping line with invalid trailer marker', { marker: fields[168] });
      continue;
    }

    const f = fields;

    // Build row with column names using underscore format
    // Field mappings per SKIFTL .dat specification v1.0.0
    const row: LonnDataRow = {
      // Header / Identification (Section 3.1)
      Skiftnr: parseDatInt(f[26]),
      Løyve: f[2],
      Sjaafor: f[3], // Driver ID
      Start_Dato: f[4],
      Start_Tid: f[5],
      Start_Dato_Tid: combineDatDateTime(f[4], f[5]),
      Slutt_Dato: f[6],
      Slutt_Tid: f[7],
      Slutt_Dato_Tid: combineDatDateTime(f[6], f[7]),

      // Trip counts and distances (Section 3.2)
      Turer: parseDatInt(f[8]),
      Total_Meter: parseDatInt(f[9]),
      Opptatt_Meter: parseDatInt(f[11]),

      // Turnover – credit, cash, subtotals (Section 3.3)
      Total_Kreditt: parseDatAmount(f[12]),
      Kreditt: parseDatAmount(f[12]),
      Total_Kroner: parseDatAmount(f[13]),
      Kontant: parseDatAmount(f[13]),
      Sub_Total: parseDatAmount(f[14]),
      Bomtur_Kroner: parseDatAmount(f[15]),
      Kreditt_Utlegg: parseDatAmount(f[16]),
      Kreditt_Tips: parseDatAmount(f[17]),
      Drivstoff: parseDatAmount(f[18]),
      Olje: parseDatAmount(f[19]),
      Vask: parseDatAmount(f[20]),
      Rekvisita: parseDatAmount(f[21]),
      Liter: parseDatInt(f[22]),
      Strombrudd: parseDatAmount(f[23]),
      Kontant_Utlegg: parseDatAmount(f[24]),

      // Cash register (Section 3.5)
      Kontant_Slutt: parseDatAmount(f[28]),
      Kontant_Start: parseDatAmount(f[29]),

      // Trip, distance, occupancy counters (Section 3.6)
      Turer_Slutt: parseDatInt(f[31]),
      Turer_Start: parseDatInt(f[32]),
      Antall_Turer: parseDatInt(f[33]),
      Meter_Slutt: parseDatInt(f[34]),
      Meter_Start: parseDatInt(f[35]),
      Opptattm_Slutt: parseDatInt(f[37]),
      Opptattm_Start: parseDatInt(f[38]),
      Opptatt: parseDatInt(f[39]),
      Kreditt_Slutt: parseDatAmount(f[40]),
      Kreditt_Start: parseDatAmount(f[41]),

      // f[43] and f[44] contain start/end datetime strings (e.g. "07:58 01,01,2026"),
      // NOT trip counts. f[45-47] contain duplicate kroner amounts, NOT trip counts.
      // These fields are intentionally skipped - datetimes are already parsed from f[4-7].

      // Actual trip count by payment type (Section 3.7)
      // Antall_Kreditturer is at f[135] (mapped below in Work time section)

      // Salary and owner share (Section 3.8)
      Lonn: parseDatAmount(f[49]),
      Skatt: parseDatAmount(f[50]),
      Uniform_Kroner: parseDatAmount(f[51]),
      Totalturavgift: parseDatInt(f[52]),
      Tur_Avgift: parseDatAmount(f[53]),
      Totalbomturavgift: parseDatAmount(f[54]),
      Bomturavgift: parseDatAmount(f[55]),
      Totalt_Eier: parseDatAmount(f[56]),
      Kontant_Eier: parseDatAmount(f[58]),

      // VAT counters (Section 3.10)
      Mva0_Slutt: parseDatAmount(f[65]),
      Mva1_Slutt: parseDatAmount(f[66]),
      Mva2_Slutt: parseDatAmount(f[67]),
      Mva3_Slutt: parseDatAmount(f[68]),
      Mva4_Slutt: parseDatAmount(f[69]),
      Mva5_Slutt: parseDatAmount(f[70]),
      Mva6_Slutt: parseDatAmount(f[71]),
      Mva7_Slutt: parseDatAmount(f[72]),
      Mva8_Slutt: parseDatAmount(f[73]),
      Mva9_Slutt: parseDatAmount(f[74]),
      Total_Mva: parseDatAmount(f[75]),
      Rest_Mva: parseDatAmount(f[76]),
      Kontant_Tips: parseDatAmount(f[77]),
      // Per-shift discounts (SKIFTL v3.2: f97/f98). NOT the cumulative
      // discount counters at f78/f79.
      Kontant_Rabatt: parseDatAmount(f[97]),
      Kreditt_Rabatt: parseDatAmount(f[98]),

      // VAT rates (Section 3.11)
      Mva_Pst0: parseDatAmount(f[80]),
      Mva_Pst1: parseDatAmount(f[81]),
      Mva_Pst2: parseDatAmount(f[82]),
      Mva_Pst3: parseDatAmount(f[83]),
      Mva_Pst4: parseDatAmount(f[84]),
      Mva_Pst5: parseDatAmount(f[85]),
      Mva_Pst6: parseDatAmount(f[86]),
      Mva_Pst7: parseDatAmount(f[87]),
      Mva_Pst8: parseDatAmount(f[88]),
      Mva_Pst9: parseDatAmount(f[89]),
      Orgnummer: f[90],

      // Tips/discount breakdowns (Section 3.12, fields 91-123)
      Kredittips: parseDatAmount(f[91]),
      Kontant_Tips2: parseDatAmount(f[92]),

      // VAT base + amount per slot 0-9 (SKIFTL v3.2). The block starts at
      // f[99] (base0/amount0) and runs to f[118] (base9/amount9): slot N is
      // base=f[99+2N], amount=f[100+2N]. So slot 2 (12%) = f103/f104 and
      // slot 4 (25%) = f107/f108. The keys keep their legacy "_type_N"
      // names; only the source index is corrected (was offset by -4, which
      // mislabelled the active 12%/25% slots). See
      // docs/migrations/SKIFTL_PARSER_REMAP_v3.2_MIGRATION.md.
      Mva_grunnlag_type_0: parseDatAmount(f[99]),
      Mva_innkjort_type_0: parseDatAmount(f[100]),
      Mva_grunnlag_type_1: parseDatAmount(f[101]),
      Mva_innkjort_type_1: parseDatAmount(f[102]),
      Mva_grunnlag_type_2: parseDatAmount(f[103]), // 12%
      Mva_innkjort_type_2: parseDatAmount(f[104]), // 12%
      Mva_grunnlag_type_3: parseDatAmount(f[105]),
      Mva_innkjort_type_3: parseDatAmount(f[106]),
      Mva_grunnlag_type_4: parseDatAmount(f[107]), // 25%
      Mva_innkjort_type_4: parseDatAmount(f[108]), // 25%
      Mva_grunnlag_type_5: parseDatAmount(f[109]),
      Mva_innkjort_type_5: parseDatAmount(f[110]),
      Mva_grunnlag_type_6: parseDatAmount(f[111]),
      Mva_innkjort_type_6: parseDatAmount(f[112]),
      Mva_grunnlag_type_7: parseDatAmount(f[113]),
      Mva_innkjort_type_7: parseDatAmount(f[114]),
      Mva_grunnlag_type_8: parseDatAmount(f[115]),
      Mva_innkjort_type_8: parseDatAmount(f[116]),
      Mva_grunnlag_type_9: parseDatAmount(f[117]),
      Mva_innkjort_type_9: parseDatAmount(f[118]),
      Grunnlag_0Pst: parseDatAmount(f[119]), // zero-rated/adjustment base (may be negative)
      Kort_Gebyr: parseDatAmount(f[120]),
      // NB: there is no "VAT start counter" block here in v3.2 — the old
      // Mva0_Start..Mva4_Start (f117-121) read into the VAT slot-9 /
      // zero-rated / card-fee / reserved positions and have been removed.

      // Cash drawer / Kasse block (fields 122-129)
      // f[122]-f[124] verified against Excel TDS reference (2026-01 data)
      Kasse_Slutt: parseDatAmount(f[122]),    // Verified: matches Excel Kasse_Slutt
      Beregnet_Kasse: parseDatAmount(f[123]), // Verified: matches Excel Beregnet_Kasse
      Diff_Kasse: parseDatAmount(f[124]),     // Verified: matches Excel Diff_Kasse
      Tilfort_Kasse: parseDatAmount(f[125]),  // Always 0 in reference data
      Lagtut_Kontant: parseDatAmount(f[126]), // Always 0 in reference data
      Lonnsprosent: parseDatAmount(f[127]),   // Percentage format "0,0"
      Skatteprosent: parseDatAmount(f[128]),  // Percentage format "0,0"
      Kasse_Start: parseDatAmount(f[129]),    // Always 0 in reference data

      // Work time and vehicle (Section 3.13)
      Effektiv_Arbeidstid_Sek: parseDatInt(f[130]),
      Total_Arbeidstid_Sek: parseDatInt(f[131]),
      Pause_Avslaattid_Sek: parseDatInt(f[132]),
      Opptatttid_Sek: parseDatInt(f[133]),
      Regnr: f[134],
      Antall_Kreditturer: parseDatInt(f[135]),
      Kontant_Skift: parseDatAmount(f[136]),
      // f[137] and f[138] are unknown fields (always 0 in reference data)
      // Kasse_Slutt and Beregnet_Kasse are correctly mapped at f[122] and f[123] above

      // Extended fields (139-165) - payment type breakdowns
      Kontant_Innkjort0: parseDatAmount(f[139]),
      Kreditt_Innkjort0: parseDatAmount(f[140]),
      Tillegg0: parseDatAmount(f[141]),
      Kontant_Innkjort1: parseDatAmount(f[142]),
      Kreditt_Innkjort1: parseDatAmount(f[143]),
      Tillegg1: parseDatAmount(f[144]),
      Kontant_Innkjort2: parseDatAmount(f[145]),
      Kreditt_Innkjort2: parseDatAmount(f[146]),
      Tillegg2: parseDatAmount(f[147]),
      Kontant_Innkjort3: parseDatAmount(f[148]),
      Kreditt_Innkjort3: parseDatAmount(f[149]),
      Tillegg3: parseDatAmount(f[150]),
      Kontant_Innkjort4: parseDatAmount(f[151]),
      Kreditt_Innkjort4: parseDatAmount(f[152]),
      Tillegg4: parseDatAmount(f[153]),
      Kontant_Innkjort5: parseDatAmount(f[154]),
      Kreditt_Innkjort5: parseDatAmount(f[155]),
      Tillegg5: parseDatAmount(f[156]),
      Kontant_Innkjort6: parseDatAmount(f[157]),
      Kreditt_Innkjort6: parseDatAmount(f[158]),
      Tillegg6: parseDatAmount(f[159]),
      Kontant_Innkjort7: parseDatAmount(f[160]),
      Kreditt_Innkjort7: parseDatAmount(f[161]),
      Tillegg7: parseDatAmount(f[162]),
      Kontant_Innkjort8: parseDatAmount(f[163]),
      Kreditt_Innkjort8: parseDatAmount(f[164]),
      Tillegg8: parseDatAmount(f[165]),

      // Final salary base and VAT (Section 3.14)
      // Lonnsgrunnlag is the MVA-free revenue base (≈ Sub_Total / 1.12).
      // These fields use dot-decimal format (e.g. "6769.57") unlike most other
      // fields which use comma-decimal. Use parseFloat directly — parseDatAmount
      // would strip the dot thinking it is a thousands separator.
      Lonnsgrunnlag: parseFloat(f[166]) || 0,
      Korrigert_MVA: parseFloat(f[167]) || 0,
    };

    rows.push(row);
  }

  return rows;
}

/**
 * All column names from the SKIFTL .dat specification
 * Used for template column selection across the app
 */
// Use var to avoid TDZ errors with webpack module bundling
export var DAT_COLUMN_NAMES: string[] = [
  // Header / Identification
  'Skiftnr', 'Løyve', 'Sjaafor', 'Start_Dato', 'Start_Tid', 'Start_Dato_Tid',
  'Slutt_Dato', 'Slutt_Tid', 'Slutt_Dato_Tid',
  // Trip counts and distances
  'Turer', 'Total_Meter', 'Opptatt_Meter',
  // Turnover – credit, cash, subtotals
  'Total_Kreditt', 'Kreditt', 'Total_Kroner', 'Kontant', 'Sub_Total', 'Bomtur_Kroner',
  'Kreditt_Utlegg', 'Kreditt_Tips', 'Drivstoff', 'Olje', 'Vask', 'Rekvisita', 'Liter',
  'Strombrudd', 'Kontant_Utlegg',
  // Cash register
  'Kontant_Slutt', 'Kontant_Start',
  // Trip, distance, occupancy counters
  'Turer_Slutt', 'Turer_Start', 'Antall_Turer', 'Meter_Slutt', 'Meter_Start',
  'Opptattm_Slutt', 'Opptattm_Start', 'Opptatt', 'Kreditt_Slutt', 'Kreditt_Start',
  // Trip counts by payment type (Antall_Kreditturer is in Work time section below)
  // Salary and owner share
  'Lonn', 'Skatt', 'Uniform_Kroner', 'Totalturavgift', 'Tur_Avgift',
  'Totalbomturavgift', 'Bomturavgift', 'Totalt_Eier', 'Kontant_Eier',
  // VAT counters
  'Mva0_Slutt', 'Mva1_Slutt', 'Mva2_Slutt', 'Mva3_Slutt', 'Mva4_Slutt',
  'Mva5_Slutt', 'Mva6_Slutt', 'Mva7_Slutt', 'Mva8_Slutt', 'Mva9_Slutt',
  'Total_Mva', 'Rest_Mva', 'Kontant_Tips', 'Kontant_Rabatt', 'Kreditt_Rabatt',
  // VAT rates
  'Mva_Pst0', 'Mva_Pst1', 'Mva_Pst2', 'Mva_Pst3', 'Mva_Pst4',
  'Mva_Pst5', 'Mva_Pst6', 'Mva_Pst7', 'Mva_Pst8', 'Mva_Pst9', 'Orgnummer',
  // Tips/discount breakdowns
  'Kredittips', 'Kontant_Tips2',
  // VAT base and innkjørt per type
  'Mva_grunnlag_type_0', 'Mva_innkjort_type_0', 'Mva_grunnlag_type_1', 'Mva_innkjort_type_1',
  'Mva_grunnlag_type_2', 'Mva_innkjort_type_2', 'Mva_grunnlag_type_3', 'Mva_innkjort_type_3',
  'Mva_grunnlag_type_4', 'Mva_innkjort_type_4', 'Mva_grunnlag_type_5', 'Mva_innkjort_type_5',
  'Mva_grunnlag_type_6', 'Mva_innkjort_type_6', 'Mva_grunnlag_type_7', 'Mva_innkjort_type_7',
  'Mva_grunnlag_type_8', 'Mva_innkjort_type_8', 'Mva_grunnlag_type_9', 'Mva_innkjort_type_9',
  'Grunnlag_0Pst', 'Kort_Gebyr',
  // Cash drawer / Kasse
  'Kasse_Slutt', 'Beregnet_Kasse', 'Diff_Kasse', 'Tilfort_Kasse', 'Lagtut_Kontant',
  'Lonnsprosent', 'Skatteprosent', 'Kasse_Start',
  // Work time and vehicle
  'Effektiv_Arbeidstid_Sek', 'Total_Arbeidstid_Sek', 'Pause_Avslaattid_Sek', 'Opptatttid_Sek',
  'Regnr', 'Antall_Kreditturer', 'Kontant_Skift',
  // Extended payment type breakdowns
  'Kontant_Innkjort0', 'Kreditt_Innkjort0', 'Tillegg0',
  'Kontant_Innkjort1', 'Kreditt_Innkjort1', 'Tillegg1',
  'Kontant_Innkjort2', 'Kreditt_Innkjort2', 'Tillegg2',
  'Kontant_Innkjort3', 'Kreditt_Innkjort3', 'Tillegg3',
  'Kontant_Innkjort4', 'Kreditt_Innkjort4', 'Tillegg4',
  'Kontant_Innkjort5', 'Kreditt_Innkjort5', 'Tillegg5',
  'Kontant_Innkjort6', 'Kreditt_Innkjort6', 'Tillegg6',
  'Kontant_Innkjort7', 'Kreditt_Innkjort7', 'Tillegg7',
  'Kontant_Innkjort8', 'Kreditt_Innkjort8', 'Tillegg8',
  // Final salary base and VAT
  'Lonnsgrunnlag', 'Korrigert_MVA',
];

/**
 * Heuristically find the driver column in a dataset
 * Looks for common column names used for driver ID
 */
export function findDriverColumn(data: LonnDataRow[]): string | null {
  if (!data || data.length === 0) {
    return null;
  }

  const firstRow = data[0];
  const columns = Object.keys(firstRow);

  // Common driver column names (case-insensitive)
  const driverColumnPatterns = [
    'sjåfør',
    'sjaafor',
    'sjafor',
    'driver',
    'driver_id',
    'driverid',
    'løyve',
    'loyve',
    'sjåførnr',
    'sjåfør nr',
    'driver nr',
  ];

  // Try exact matches first (case-insensitive)
  for (const pattern of driverColumnPatterns) {
    const match = columns.find(
      col => col.toLowerCase() === pattern.toLowerCase()
    );
    if (match) {
      return match;
    }
  }

  // Try partial matches
  for (const pattern of driverColumnPatterns) {
    const match = columns.find(
      col => col.toLowerCase().includes(pattern.toLowerCase())
    );
    if (match) {
      return match;
    }
  }

  return null;
}

/**
 * Apply kontant edits to filtered data
 * Modifies the data in place based on edit records
 * Attaches note_numbers array to rows that have edits
 * Supports multiple edits per shift (whole-shift + receipt-specific)
 */
export function applyKontantEdits(
  data: LonnDataRow[],
  edits: KontantEdit[]
): LonnDataRow[] {
  if (!edits || edits.length === 0) {
    return data;
  }

  // Create a map of edits by (loyve, skiftnr) for quick lookup
  // Each shift can have multiple edits (whole-shift + receipt-specific)
  const editsMap = new Map<string, KontantEdit[]>();
  edits.forEach(edit => {
    const key = `${edit.loyve}|||${edit.skiftnr}`;
    const existing = editsMap.get(key) || [];
    existing.push(edit);
    editsMap.set(key, existing);
  });

  // Apply edits to matching rows
  return data.map(row => {
    // Try to find loyve and skiftnr columns
    const loyveColumn = findLoyveColumn(row);
    const skiftnrColumn = findSkiftnrColumn(row);

    if (!loyveColumn || !skiftnrColumn) {
      return row;
    }

    // Normalize the row's loyve and skiftnr
    const rowLoyve = normalizeLoyve(String(row[loyveColumn] || ''));
    const rowSkiftnr = normalizeSkiftnr(String(row[skiftnrColumn] || ''));

    const key = `${rowLoyve}|||${rowSkiftnr}`;
    const shiftEdits = editsMap.get(key);

    if (shiftEdits && shiftEdits.length > 0) {
      // Calculate total difference from all edits for this shift
      const totalDifference = shiftEdits.reduce((sum, edit) => sum + edit.difference, 0);

      // Get original kontant value
      const kontantColumn = findKontantColumnInRow(row);
      if (kontantColumn) {
        const originalKontant = getNumericValue(row[kontantColumn]);
        const finalKontant = originalKontant + totalDifference;

        // Collect all note numbers for this shift
        const noteNumbers = shiftEdits.map(e => e.note_number).sort((a, b) => a - b);

        return {
          ...row,
          [kontantColumn]: finalKontant,
          note_numbers: noteNumbers, // Attach array of note numbers
          has_notes: true, // Flag for easy checking
        };
      }
    }

    return row;
  });
}

/**
 * Find the loyve column in a row
 */
function findLoyveColumn(row: LonnDataRow): string | null {
  const loyveColumnPatterns = [
    'løyve',
    'loyve',
    'loeyve',
    'license',
  ];

  const columns = Object.keys(row);

  for (const pattern of loyveColumnPatterns) {
    const match = columns.find(
      col => col.toLowerCase() === pattern.toLowerCase()
    );
    if (match) {
      return match;
    }
  }

  return null;
}

/**
 * Find the skiftnr column in a row
 */
function findSkiftnrColumn(row: LonnDataRow): string | null {
  const skiftnrColumnPatterns = [
    'skiftnr',
    'shift_nr',
    'shiftnr',
    'shift_number',
  ];

  const columns = Object.keys(row);

  for (const pattern of skiftnrColumnPatterns) {
    const match = columns.find(
      col => col.toLowerCase() === pattern.toLowerCase()
    );
    if (match) {
      return match;
    }
  }

  return null;
}

/**
 * Normalize loyve value for consistent matching
 * Exported for use in cash editor
 */
export function normalizeLoyve(value: string): string {
  // Remove common prefixes like "R-" or "R " and trim
  return value
    .replace(/^[rR][-\s]*/, '')
    .trim()
    .toUpperCase();
}

/**
 * Normalize skiftnr value for consistent matching
 * Exported for use in cash editor
 */
export function normalizeSkiftnr(value: string): string {
  // Trim and convert to string
  return value.trim();
}

/**
 * Find loyve column in dataset (exported for cash editor)
 */
export function findLoyveColumnInData(data: LonnDataRow[]): string | null {
  if (!data || data.length === 0) return null;
  return findLoyveColumn(data[0]);
}

/**
 * Find skiftnr column in dataset (exported for cash editor)
 */
export function findSkiftnrColumnInData(data: LonnDataRow[]): string | null {
  if (!data || data.length === 0) return null;
  return findSkiftnrColumn(data[0]);
}

/**
 * Find the kontant column in a row
 */
function findKontantColumnInRow(row: LonnDataRow): string | null {
  const kontantColumnPatterns = [
    'kontant',
    'cash',
    'kontant_kroner',
  ];

  const columns = Object.keys(row);

  for (const pattern of kontantColumnPatterns) {
    const match = columns.find(
      col => col.toLowerCase() === pattern.toLowerCase()
    );
    if (match) {
      return match;
    }
  }

  return null;
}

/**
 * Get numeric value from a cell (handles strings and numbers)
 */
export function getNumericValue(value: any): number {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    // Remove spaces and replace comma with dot for Norwegian format
    const cleaned = value.replace(/\s/g, '').replace(',', '.');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }

  return 0;
}

/**
 * Check if a value is numeric (can be converted to a number)
 */
export function isNumericValue(value: any): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  if (typeof value === 'number') {
    return !isNaN(value);
  }

  if (typeof value === 'string') {
    const cleaned = value.replace(/\s/g, '').replace(',', '.');
    const parsed = parseFloat(cleaned);
    return !isNaN(parsed);
  }

  return false;
}

/**
 * Format number in Norwegian style (space for thousands, comma for decimals)
 * Uses only ASCII characters for jsPDF compatibility
 * Avoids Unicode spaces (U+00A0, U+2009, U+202F) that cause rendering issues in PDFs
 */
export function formatNorwegianNumber(value: number): string {
  // Handle edge cases
  if (!Number.isFinite(value)) {
    return '0';
  }

  // Determine if we need decimals
  const hasDecimals = !Number.isInteger(value);

  // Format with the appropriate decimal places
  const absValue = Math.abs(value);
  const fixedValue = hasDecimals ? absValue.toFixed(2) : Math.round(absValue).toString();

  // Split into integer and decimal parts
  const parts = fixedValue.split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1];

  // Add thousands separators (regular ASCII space)
  let formatted = '';
  const digits = integerPart.split('').reverse();
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && i % 3 === 0) {
      formatted = ' ' + formatted;
    }
    formatted = digits[i] + formatted;
  }

  // Add decimal part with comma (Norwegian style)
  if (decimalPart) {
    formatted += ',' + decimalPart;
  }

  // Add negative sign if needed
  if (value < 0) {
    formatted = '-' + formatted;
  }

  return formatted;
}

/**
 * Calculate column totals for numeric columns
 */
export function calculateColumnTotals(
  data: LonnDataRow[],
  columns: string[]
): Record<string, number> {
  const totals: Record<string, number> = {};

  columns.forEach(column => {
    let sum = 0;
    let hasNumericValues = false;

    data.forEach(row => {
      const value = row[column];
      if (isNumericValue(value)) {
        sum += getNumericValue(value);
        hasNumericValues = true;
      }
    });

    // Only include columns that have at least one numeric value
    if (hasNumericValues) {
      totals[column] = sum;
    }
  });

  return totals;
}
